import SaveAltIcon from "@material-ui/icons/SaveAlt"
import AppLayout from "@/components/AppLayout"
import LoginLoader from "@/components/LoginLoader"
import PptCheckbox from "@/components/PptCheckbox"
import {useState, useEffect, FormEvent} from "react"
import PptInputTailwind from "@/components/PptInputTailwind"
import {friendlyPhone} from "@/lib/utils"
import {isEmail} from "@/lib/validation/index"
import {
  CANADA_AND_US_COUNTRY_CODE,
  ONLY_NUMBERS_REGEX,
  MAX_PHONE_NUMBER_LENGTH,
  COMPLETE_PHONE_NUMBER_LENGTH,
  COUNTRY_CODE_CHARACTER_LENGTH
} from "@/lib/consts"
import type {ContactInfo} from "./contact"
import {trpc} from "@/lib/requests/index"
import {useRouter} from "next/router"

const PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS = "w-5/6"
const EMAIL_AND_TEXT_TOGGLE_WIDTH_CLASS = "w-4/4"

export default function Notifications(): JSX.Element {
  const router = useRouter()
  const trpcContext = trpc.useContext()
  const userSettingsQuery = trpc.useQuery(["user.settings"])
  const notificationMut = trpc.useMutation(["user.updateSettings"])
  const userContactQuery = trpc.useQuery(
    ["user.contact"], {enabled: false}
  )
  // For some reason whenever overwriting cache for "user.contact"
  // trpc insists on refetching data from server right after.
  // This makes it appear that data has not been changed on the 
  // frontend (when removing a phone number for example).
  // To avoid this problem, I'm not allowing the query to run
  // whenever it likes.
  useEffect(() => { userContactQuery.refetch() }, [])
  const updateContactMut = trpc.useMutation(["user.updateContact"])
  function handleUpdateContact<
    T extends keyof ContactInfo
  >(key: T, value: ContactInfo[T]) {
    trpcContext.setQueryData(["user.contact"], (old) => {
      if (!old) {
        return {} as NonNullable<typeof old>
      }
      const newContact = {
        ...old,
        contact: {...old.contact, [key]: value}
      } as NonNullable<typeof old>
      return newContact
    })
    updateContactMut.mutate({[key]: value})
  }

  function updateSettings(
    key: "notifyEmail" | "notifySms",
    value: boolean
  ) {
    trpcContext.setQueryData(["user.settings"], (old) => {
      if (!old) {
        return {} as NonNullable<typeof old>
      }
      const newData = {...old, [key]: value}
      return newData as typeof old
    })
    notificationMut.mutate({[key]: value})
  }

  const [newPhoneNumber, setNewPhoneNumber] = useState(CANADA_AND_US_COUNTRY_CODE)
  const [newEmail, setNewEmail] = useState("")

  const contactInfo = userContactQuery.data?.contact

  function handleNewPhoneNumberChange(newPhoneNumber: string) {
    if (!contactInfo) {
      return
    }
    if (newPhoneNumber.length <= COUNTRY_CODE_CHARACTER_LENGTH) {
      return setNewPhoneNumber(CANADA_AND_US_COUNTRY_CODE)
    }
    const phoneSection = newPhoneNumber.slice(COUNTRY_CODE_CHARACTER_LENGTH)
    const longerThanTenDigits = phoneSection.length > MAX_PHONE_NUMBER_LENGTH
    const phoneNumberAlreadyExists = contactInfo.notificationPhoneNumbers.find(p => p === newPhoneNumber)
    if (!containsOnlyNumbers(phoneSection.toString()) || longerThanTenDigits || phoneNumberAlreadyExists) {
      return
    } else {
      setNewPhoneNumber(`${CANADA_AND_US_COUNTRY_CODE}${phoneSection}`)
    }
  }

  function handleEmailContactUpdate(emails: string[]) {
    return handleUpdateContact("notificationEmails", emails)
  }

  function removeEmailFromContact(email: string) {
    if (!contactInfo) {
      return
    }
    if (!window.confirm("Are you sure you want to remove this email?")) {
      return
    }
    const updatedEmails = contactInfo.notificationEmails.filter(e => e !== email)
    handleEmailContactUpdate(updatedEmails)
  }

  function addEmailToContact(email: string) {
    if (!contactInfo) {
      return
    }
    const updatedEmails = [...contactInfo.notificationEmails, email]
    handleEmailContactUpdate(updatedEmails)
  }

  function handlePhoneNumberContactUpdate(phoneNumbers: string[]) {
    return handleUpdateContact("notificationPhoneNumbers", phoneNumbers)
  }

  function removePhoneNumberFromContact(phoneNumber: string) {
    if (!contactInfo) {
      return
    }
    if (!window.confirm("Are you sure you want to remove this phone number?")) {
      return
    }
    const updatedPhoneNumbers = contactInfo.notificationPhoneNumbers.filter(
      p => p !== phoneNumber
    )
    handlePhoneNumberContactUpdate(updatedPhoneNumbers)
  }

  function addPhoneNumberToContact(phoneNumber: string) {
    if (!contactInfo) {
      return
    }
    const updatedPhoneNumbers = [...contactInfo.notificationPhoneNumbers, phoneNumber]
    handlePhoneNumberContactUpdate(updatedPhoneNumbers)
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.push("/account")
  }

  const loading = (
    userSettingsQuery.isLoading 
    || userContactQuery.isLoading
  )

  const error = (
    userSettingsQuery.isError 
    || userContactQuery.isError
  )

  return <AppLayout requiredAuth="user">
    {loading ? (
      <div className="w-full h-full flex items-center flex-col justify-center">
        <LoginLoader></LoginLoader>
      </div>
    ) : null}
    {error ? (
      <pre>
        {"Error: "}
        {userSettingsQuery.error || userContactQuery.error}
      </pre>
    ) : null}
    {userSettingsQuery.data && userContactQuery.data ? (
      <form className="app-layout" onSubmit={handleSubmit}>
        <section className="app-layout-content flex flex-col items-center p-4 relative">
          <div className="flex flex-col items-center my-12">
            <h1 className="text-3xl sm:text-4xl font-display">
              {"Notifications"}
            </h1>
          </div>
          <div className="box-content my-8 p-4 w-3/4">
            <h3 className="text-xl ">{"When I get a new lead..."}</h3>
            <PptCheckbox
              name="notify-email"
              isChecked={userSettingsQuery.data.notifyEmail}
              onChange={() => {
                const prev = userSettingsQuery.data?.notifyEmail
                updateSettings("notifyEmail", !prev)
              }}
              className={EMAIL_AND_TEXT_TOGGLE_WIDTH_CLASS}
              labelText={`Send Email Notifications`}
            />
            <PptCheckbox
              name="notify-sms"
              isChecked={userSettingsQuery.data.notifySms}
              onChange={() => {
                const prev = userSettingsQuery.data?.notifySms
                updateSettings("notifySms", !prev)
              }}
              className={EMAIL_AND_TEXT_TOGGLE_WIDTH_CLASS}
              labelText={`Send Text Notifications`}
            />

            { userSettingsQuery.data.notifyEmail || userSettingsQuery.data.notifySms ?
              <> 
                <br/>
                <div className="w-4/4 flex flex-row justify-evenly items-start">
                  
                  { userSettingsQuery.data.notifyEmail && contactInfo ?
                    <div className="w-2/4">
                      <h3 className="text-xl ">{"Send an Email To:"}</h3>

                      {
                        contactInfo?.notificationEmails.map((e, index) => {
                          return <PptCheckbox
                              name={e + "-email"}
                              key={index}
                              isChecked
                              onChange={() => removeEmailFromContact(e)}
                              className={PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS}
                              labelText={e}
                          />
                        })
                      }
                      {
                        contactInfo.notificationEmails.length > 0 ? 
                          <p className="text-xs text-red-600">
                            {"Click to Remove"}
                          </p> : <h4 className="text-base text-red-600 mt-2">
                            {"No Emails on File"}
                          </h4>
                      }
  
                      <br/>
                        <div className="w-4/4">
                          
                          <PptInputTailwind
                                className="col-span-3 w-full focus:outline-none"
                                labelText="Add Email"
                                inputClassName={PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS}
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                autoComplete="family-name"
                          />
                          
                          <div className={`${PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS} `}>
                            <button 
                              className="w-full bg-blue-900 hover:bg-blue-700 p-2"
                              onClick={() => {
                                addEmailToContact(newEmail)
                                setNewEmail("")
                              }}
                              disabled={!isEmail(newEmail)}
                            >
                              Submit
                            </button>
                          </div>
                        </div>
                      <br/>
                    </div> : null
                  }
  
                  { userSettingsQuery.data.notifySms && contactInfo ?
                    <div className="w-2/4">
                      <h3 className="text-xl ">{"Send a Text To:"}</h3>
                      {
                        contactInfo.notificationPhoneNumbers.map((p, index) => {
                          return <PptCheckbox
                              name={p + "-phone-number"}
                              key={index}
                              isChecked
                              onChange={() => removePhoneNumberFromContact(p)}
                              className={PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS}
                              labelText={friendlyPhone(p)}
                          />
                        })
                      }
                      {
                        contactInfo.notificationPhoneNumbers.length > 0 ? 
                          <p className="text-xs text-red-600">Click to Remove</p> : 
                          <h4 className="text-base text-red-600 mt-2">
                            {"No Phone Numbers on File"}
                          </h4>
                      }
                      
  
                      <br/>
                        <div className="w-4/4">
                          <PptInputTailwind
                            className="col-span-3 w-full focus:outline-none"
                            labelText="Add Phone Number"
                            inputClassName={PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS}
                            value={newPhoneNumber}
                            onChange={(e) => handleNewPhoneNumberChange(e.target.value)}
                            autoComplete="family-name"
                          />
                          
                          <div className={`${PHONE_NUMBER_AND_EMAIL_WIDTH_CLASS} `}>
                            <button 
                              className="w-full bg-blue-900 hover:bg-blue-700 p-2 disabled:cursor-not-allowed"
                              disabled={newPhoneNumber.length !== COMPLETE_PHONE_NUMBER_LENGTH}
                              onClick={() => {
                                addPhoneNumberToContact(newPhoneNumber)
                                setNewPhoneNumber("")
                              }}
                            >
                              Submit
                            </button>
                          </div>
                          
                        </div>
                      <br/>
                    </div> : null
                  }
                  
                </div>
              </> : null
            }
        
          </div>
        </section>
        <div className="w-2/12 h-full pb-20 flex flex-col justify-end items-center">
          <button
            type="submit"
            className={
              "flex items-center font-display text-3xl sm:text-4xl mt-4" +
              " text-ppt-blue hover:text-ppt-success border-b-4 border-transparent" +
              " hover:border-ppt-success"
            }
          >
            <SaveAltIcon
              style={{
                width: "2rem",
                height: "2rem",
                marginRight: "0.25rem",
              }}
            />
            {"save"}
          </button>
        </div>
      </form>
    ) : null}
  </AppLayout>
}

function containsOnlyNumbers(str: string): boolean {
  return ONLY_NUMBERS_REGEX.test(str)
}

